import { Button } from '@app/components/various/Button';
import { AddSquareOutline } from '@app/components/various/Icon/Icons/cms/AddSquareOutline';
import { Paste } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { setCurrentGroup, setCurrentSection, showModal } from '@app/ducks';
import { cn } from '@app/utils/cn';
import { useGroupCopyPaste } from '@app/utils/hooks/cms/useGroupCopyPaste';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { EditorUIContext } from '../context/EditorUIContext';
import { GroupResizer } from '../GroupResizer';

interface Props {
  groupId?: string;
  groupIndex: number;
  sectionId: string;
  resizableBlocksIds?: Set<string>;
}

export const GroupButtons = ({ groupId, groupIndex, sectionId, resizableBlocksIds }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['cms', 'common']);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const { screenType } = React.useContext(EditorUIContext);
  const { hasGroupInClipboard, pasteCopiedGroup } = useGroupCopyPaste();

  const isMobileScreenType = screenType === 'mobile';

  const handleAddGroup = () => {
    if (isDefined(groupId)) {
      dispatch(setCurrentGroup(groupId));
    }

    dispatch(setCurrentSection(sectionId));
    dispatch(showModal('AddTemplateModal'));
  };

  const handlePasteGroup = () => {
    pasteCopiedGroup(groupIndex, sectionId);
  };

  const canResize = isDefined(resizableBlocksIds) && resizableBlocksIds.size > 0;

  return (
    <div
      ref={wrapperRef}
      className={cn('absolute left-1/2 z-elevated grid -translate-x-1/2 grid-cols-[1fr_auto_1fr] justify-center gap-6', 'groupButtons', {
        '-bottom-3': groupIndex !== 0,
        '-top-3': groupIndex === 0,
      })}
    >
      {canResize && isDefined(resizableBlocksIds) ?
        <GroupResizer parentRef={wrapperRef} resizableBlocksIds={resizableBlocksIds} />
      : <div />}
      <Tooltip content={isMobileScreenType && t('cms:add_layout')}>
        <Button
          size="medium"
          color="secondary"
          variant={hasGroupInClipboard ? 'solid' : 'bordered'}
          data-testid="addLayoutButton"
          className="h-6 bg-neutral-96"
          icon={AddSquareOutline}
          onClick={handleAddGroup}
        >
          {!isMobileScreenType && t('cms:add_layout')}
        </Button>
      </Tooltip>
      {hasGroupInClipboard && (
        <Tooltip content={isMobileScreenType && t('common:paste')}>
          <Button
            size="medium"
            color="secondary"
            variant="bordered"
            data-testid="pasteLayoutButton"
            icon={Paste}
            onClick={handlePasteGroup}
            className="h-6 bg-neutral-96"
          >
            {!isMobileScreenType && t('common:paste')}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
